/**
 * Access-code generation and hand-off, for the Regulated tier.
 *
 * A Regulated envelope requires a per-signer access code — the second factor
 * that stops an emailed signing link from being the whole credential. The
 * sender chooses it, tells the signer out of band, and the server stores only
 * a scrypt hash.
 *
 * # Why generation belongs here and not on the server
 *
 * The code has to reach a human by a channel Fias is not part of — a phone
 * call, or a conversation. A server-generated code would have to be returned
 * over the wire and rendered, which means it exists in a response body, a
 * query cache and quite possibly a log, for a value whose entire security
 * property is that it travelled a different path from the link. Generating in
 * the browser keeps the plaintext in one place for one page's lifetime.
 *
 * That is also why there is no "show me the code again": it is unrecoverable
 * by construction, and the UI has to say so at the moment it is offered.
 */

/**
 * Unambiguous alphabet — no `0`/`O`, no `1`/`l`/`I`.
 *
 * These codes are read down a phone line, and a signer gets five attempts
 * before being locked out (`DOCUMENT_SIGN_ACCESS_CODE_MAX_ATTEMPTS`). A
 * character pair that sounds or looks alike spends one of those attempts on
 * the sender's font choice.
 *
 * Exactly 32 characters, which also makes `byte % 32` unbiased — a rejection
 * loop would be the alternative, and getting it subtly wrong is a quiet way to
 * lose entropy.
 *
 * ## Why the `gitleaks:allow` below
 *
 * gitleaks' `generic-api-key` rule reads this as a credential, and its
 * reasoning is sound in general: a constant whose name contains `ACCESS`,
 * assigned a quoted 32-character alphanumeric literal with high entropy, is
 * usually a key someone pasted in. Here the entropy is the tell in the
 * opposite direction — every character is DISTINCT, because this is the set of
 * symbols a code may be drawn FROM, not a code. It is public by construction:
 * it ships in the client bundle, and an attacker who knows the alphabet gains
 * nothing they could not read off a generated code.
 *
 * The suppression is deliberately inline and one line wide. A path entry in
 * `.gitleaks.toml` would have been less typing and much worse: it would blind
 * the scanner to this whole file — and to anything later added to it — for
 * every future PR, which is precisely how a real key eventually walks in
 * behind a false positive. Scoped here, the exemption cannot outlive the line
 * it is attached to.
 *
 * The directive has to sit on the SAME line as the match — gitleaks tests the
 * matched line for it, so a comment on the line above is silently inert.
 */
export const ACCESS_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'; // gitleaks:allow

/**
 * Generated length. 8 characters over a 32-symbol alphabet is 40 bits — far
 * past anything the 5-attempt online cap exposes, and still short enough to
 * dictate in one breath. The server accepts 4–64, so this sits comfortably
 * inside a sender's own choice either way.
 */
export const GENERATED_ACCESS_CODE_LENGTH = 8;

/**
 * A fresh access code.
 *
 * `crypto.getRandomValues`, never `Math.random`. This is a credential; a
 * predictable one would make the access code a formality while looking
 * identical in the UI.
 */
export function generateAccessCode(length = GENERATED_ACCESS_CODE_LENGTH): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) out += ACCESS_CODE_ALPHABET[byte % ACCESS_CODE_ALPHABET.length];
  return out;
}

export interface AccessCodeHandoffEntry {
  name: string;
  email: string;
  code: string;
}

/**
 * The text "Copy all" puts on the clipboard.
 *
 * Pairs, not bare codes. The sender is about to read these to people one at a
 * time; a column of codes with no names is something they have to re-derive
 * from row order, and getting it wrong sends someone else's code to a signer
 * who then burns attempts on it.
 *
 * Entries with no code are skipped rather than rendered blank — a half-filled
 * list that looks complete is worse than a short one.
 */
export function formatAccessCodeHandoff(entries: AccessCodeHandoffEntry[]): string {
  return entries
    .filter((e) => e.code.trim() !== '')
    .map((e) => {
      const who = e.name.trim() || e.email.trim() || 'Unnamed signer';
      return `${who} — ${e.code.trim()}`;
    })
    .join('\n');
}
